import { useContext, useSyncExternalStore } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { AuthContext } from '@/context/auth-context'
import { AnalyticsEvent, AnalyticsEventProperties } from '@/analytics/schema/events.schema.ts'
import { DataLayerObject } from '@/analytics/schema/tag-manager.schema.ts'
import type { RootState } from '@/store/store'
import { ReactReduxContext } from 'react-redux'
import Cookies from 'js-cookie'
import { matchPath, UNSAFE_LocationContext, useParams } from 'react-router-dom'
import { setTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { kAnalyticsFeatureAgent, kAnalyticsFeatureDraftPage } from '@/analytics/constants'
import { kRouteSegments, routePatterns } from '@/routing/paths'
import { isOutlookPrivacyMode } from '@/analytics/privacy/outlook-continuation-privacy'

const noopSubscribe = () => () => {}

// Keys that should always persist between events and never be cleaned
const GLOBAL_KEYS = new Set<keyof DataLayerObject>([
  'event',
  'userEmail',
  'userId',
  'activeMatterId',
  'subscriptionStatus',
  'orgSubscriptionStatus',
  'hubspotUtk',
  'transaction_id',
])

// Snapshot of keys sent in the previous event (excluding GLOBAL_KEYS)
let lastVolatileKeys: string[] = []

// Reusable type for the trackEvent callback
export type TrackEventFn = <T extends AnalyticsEvent>(
  event: T,
  properties?: (AnalyticsEventProperties[T] & Record<string, string | number | boolean>) | undefined,
  extraOpts?: { suppressFeatureInheritance?: boolean }
) => void

declare global {
  interface Window {
    dataLayer: Array<DataLayerObject>
    trackEventGlobal?: TrackEventFn
  }
}

const getHubSpotTrackingCookie = (): string | undefined => {
  return Cookies.get('hubspotutk')
}

export const useAnalytics = () => {
  const { user, userAccountData } = useContext(AuthContext)
  const { chatFeature } = useParams()
  // AuthContextProvider calls this hook above the router. useMatch/useLocation throw there and
  // blank the page. The location context is null outside a router; the event-time pathname covers that.
  const locationContext = useContext(UNSAFE_LocationContext)
  // Read the store through its context so the hook also runs with no Redux provider mounted (for example in an
  // MCP app), reporting no matter id instead of throwing.
  const reduxContext = useContext(ReactReduxContext)
  const activeMatterId = useSyncExternalStore(
    reduxContext?.store.subscribe ?? noopSubscribe,
    () => (reduxContext?.store.getState() as RootState | undefined)?.mattersState.activeMatterId
  )

  // Grab HubSpot tracking cookie
  const hubspotUtk = getHubSpotTrackingCookie()

  const trackEvent = <T extends AnalyticsEvent>(
    event: T,
    properties?: Omit<AnalyticsEventProperties[T], 'userId' | 'subscriptionStatus' | 'orgSubscriptionStatus'> & Record<string, string | number | boolean>,
    extraOpts: { suppressFeatureInheritance?: boolean } = {}
  ) => {
    if (isOutlookPrivacyMode()) return
    // Analytics is observational: delivery must never block navigation, downloads, saves or any
    // other product behavior. GTM replaces `dataLayer.push` with its own implementation that
    // evaluates triggers and runs tag templates synchronously, so a broken tag would otherwise
    // throw straight into whatever product code emitted the event.
    //
    // Swallowed rather than reported, matching Sentry's existing analytics deny rules in
    // `src/main.tsx` and the other analytics helpers. A broken tag fails on every emit for every
    // user, so reporting would flood Sentry — and it would reach engineers rather than the Data
    // team who own GTM. Events that stop arriving are detected by an Amplitude volume monitor.
    try {
      emitToDataLayer(event, properties, extraOpts)
    } catch {
      // Observational only — delivery must not affect any product control-flow.
    }
  }

  const emitToDataLayer = <T extends AnalyticsEvent>(
    event: T,
    properties?: Omit<AnalyticsEventProperties[T], 'userId' | 'subscriptionStatus' | 'orgSubscriptionStatus'> & Record<string, string | number | boolean>,
    extraOpts: { suppressFeatureInheritance?: boolean } = {}
  ) => {
    const transaction_id = uuidv4()
    window.dataLayer = window.dataLayer || []

    // Callers may type a literal `feature` into their event's properties (e.g. MedSummary*,
    // MatterDriveSync*) to explicitly tag a feature that doesn't depend on the current route.
    // That always wins over route inheritance/suppression below.
    const explicitFeature = typeof properties?.feature === 'string' ? properties.feature : undefined

    // Normalize both current chat routes and inbound alias URLs to the same analytics feature.
    // The drafts list is not a chat route; it gets its own feature instead of inheriting 'agent'.
    const isChatRoute = chatFeature === kRouteSegments.assistant || chatFeature === kRouteSegments.agentV2
    const pathname = locationContext?.location.pathname ?? window.location.pathname
    const isDraftsRoute = matchPath({ path: routePatterns.drafts, end: true }, pathname) != null
    const routeFeature = isChatRoute ? kAnalyticsFeatureAgent : isDraftsRoute ? kAnalyticsFeatureDraftPage : undefined
    const normalizedFeature = explicitFeature ?? (extraOpts.suppressFeatureInheritance ? undefined : routeFeature)

    const eventData: DataLayerObject = {
      event,
      userEmail: user?.email || '',
      userId: user?.uid || '',
      activeMatterId: activeMatterId || '',
      subscriptionStatus: userAccountData?.subscriptionStatus,
      ...properties,
      feature: normalizedFeature || '',
      hubspotUtk,
      transaction_id,
    }

    // --- Dynamic cleaning of inherited properties ---
    // Add empty string for any key that existed in the previous event but is missing now
    lastVolatileKeys.forEach((k) => {
      if (!(k in eventData)) {
        ;(eventData as unknown as Record<string, unknown>)[k] = ''
      }
    })

    // If orgData exists and has subscription_status, we add it
    if (userAccountData?.orgData?.subscription_status) {
      eventData.orgSubscriptionStatus = userAccountData.orgData.subscription_status
    }

    window.dataLayer.push(eventData)

    // Update snapshot of volatile keys for next event
    lastVolatileKeys = Object.keys(eventData).filter((k) => !GLOBAL_KEYS.has(k as keyof DataLayerObject))
  }

  // Expose globally for use in non-React contexts (Redux listeners, WebSocket handlers, etc.)
  setTrackEventGlobal(trackEvent)

  return { trackEvent }
}
